import { Injectable } from '@angular/core';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { IOnboardingItemBase } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiOnboardingService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding.service';
import { FiOnboardingBuilderHelperService } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding-builder-helper.service';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';
import { FiOnboardingReportDataService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-data.service';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { IDialogConfirmDeleteData } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete-data';
import { FiDialogConfirmDeleteComponent } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete.component';

interface IBuildOnboardingActionsArgs<T> {
    onOnboardingDeletedFn: (item: T) => void;
    stepElementObjectIds: number[];
    stepElementObjectTypes: ObjectTypes[];
}

@Injectable()
export class FiOnboardingActionsBuilderService {
    constructor(
        private appTextService: AppTextService,
        private fiStateService: FiStateService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiDialogsService: FiDialogsService,
        private fiOnboardingService: FiOnboardingService,
        private fiOnboardingHelperService: FiOnboardingHelperService,
        private fiOnboardingReportDataService: FiOnboardingReportDataService,
    ) {}

    getOnboardingActions<T extends IOnboardingItemBase>(args: IBuildOnboardingActionsArgs<T>): IItemsListAction2<T>[] {
        const hasShareRights = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.OnboardingLocal,
            AccessRightType.Share,
        );

        const actions: IItemsListAction2<T>[] = [
            {
                title: this.appTextService.get('onboarding.edit_onboarding'),
                condition: (item) => hasShareRights && item.HasEditAccess,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-pen-to-square',
                onClick: (item) => {
                    const stateName = this.fiOnboardingHelperService.isCommonOnboarding(item.Category)
                        ? FiStateNames.Onboarding.Edit
                        : FiStateNames.Onboarding.Connection.Edit; // todo
                    this.fiStateService.go(stateName, {
                        id: item.Id,
                        stepElementObjectIds: args.stepElementObjectIds,
                        stepElementObjectTypes: args.stepElementObjectTypes,
                    });
                },
            },
            {
                title: this.appTextService.get('onboarding.copy_onboarding'),
                condition: (item) => hasShareRights,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-copy',
                onClick: (item) => this.fiStateService.go(FiStateNames.Onboarding.Copy, { id: item.Id }),
            },
            {
                title: this.appTextService.get('checklist.view_report'),
                condition: (item) => item.HasReportsAccess && item.IsActive,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-chart-pie',
                onClick: (item) => {
                    this.fiOnboardingReportDataService.viewOnboardingReport(item.Id);
                },
            },
            this.getDeleteAction(hasShareRights, args.onOnboardingDeletedFn),
        ];

        return actions;
    }


    private getDeleteAction<T extends IOnboardingItemBase>(hasShareRights: boolean, onOnboardingDeletedFn: (item: T) => void): IItemsListAction2<T> {
        return {
            title: this.appTextService.get('onboarding.delete_onboarding'),
            condition: (item) => hasShareRights && item.HasEditAccess,
            optionType: FiItemsListOptionType.Icon,
            iconClass: 'fa-regular fa-trash',
            onClick: (item) => {
                const deleteFn = () =>
                    this.fiOnboardingService.deleteOnboardings([item.Id]).then(() => {
                        onOnboardingDeletedFn(item);
                    });
                const objectTypeName = this.appTextService
                    .get(getObjectTypeNameTextId(ObjectTypes.Onboarding))
                    .toLowerCase();

                if (!item.HasCompletions) {
                    this.fiDialogsService
                        .confirm({
                            headerText: this.appTextService.get('common.delete_item', {
                                '{0}': objectTypeName,
                            }),
                            messageText: this.appTextService.get('onboarding.delete_confirm_message'),
                            yesButtonText: this.appTextService.get('common.delete'),
                            headerIconClass: 'fa-regular fa-circle-exclamation red',
                            isBigSizeTopBorder: true,
                            colorClass: 'red',
                            config: {
                                panelClass: ['fi-rounded-dialog-panel'],
                            },
                        })
                        .onYesButtonClick(() => deleteFn());
                } else {
                    const dialogContentData: IDialogConfirmDeleteData = {
                        step1: {
                            message: this.appTextService.get('onboarding.delete_with_completions_confirm_message'),
                            buttonText: this.appTextService.get('common.confirm'),
                            confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                        },
                    };

                    this.fiDialogsService
                        .custom({
                            headerText: this.appTextService.get('common.delete_item', {
                                '{0}': objectTypeName,
                            }),
                            contentComponentRef: FiDialogConfirmDeleteComponent,
                            contentComponentData: dialogContentData,
                            headerIconClass: 'fa-regular fa-circle-exclamation red',
                            isBigSizeTopBorder: true,
                            colorClass: 'red',
                            config: {
                                panelClass: ['fi-rounded-dialog-panel'],
                            },
                        })
                        .onYesButtonClick(() => deleteFn());
                }
            },
        }
    }
}
